import { Component } from '@angular/core';
import Swal from 'sweetalert2';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { UserRoles } from 'src/app/shared/enums/roles-enum';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { etiquetaRed, MEDIO_CRIPTO_TRC20 } from 'src/app/shared/utils/red-cripto';
import { HoursOperationService } from '../../services/hours-operation.service';
import { MedioCripto, MediosCriptoService } from '../../services/medios-cripto.service';
import { ParameterService } from '../../services/parameter.service';

@Component({
  selector: 'app-hours-operations',
  templateUrl: './hours-operations.component.html',
  styleUrls: ['./hours-operations.component.sass'],
})
export class HoursOperationsComponent {
  role = UserRoles.aguacate_operator;
  expandedSections: Set<string> = new Set();

  toggleSection(section: string) {
    if (this.expandedSections.has(section)) {
      this.expandedSections.delete(section);
    } else {
      this.expandedSections.add(section);
    }
  }

  estadoOperacion = {
    FIAT: true,
    USDT: true,
    ZELLE: true,
    AC: true,
    FIAT_STOP: true,
    USDT_STOP: true,
    ZELLE_STOP: true,
  };

  estadoBinance = false;
  pagoMovilGlobal = false;
  pagoMovilBanesco = false;

  /**
   * Redes cripto de los beneficiarios (`medio_criptomonedas`), tal como las
   * tiene Nest. `null` mientras carga o si la lectura falló: en ese caso no
   * se ofrecen los botones, porque pausar o activar sobre un estado que no se
   * pudo leer es apretar a ciegas.
   */
  redesCripto: MedioCripto[] | null = null;
  errorRedes = false;

  constructor(
    private readonly hoursOperationService: HoursOperationService,
    private paramsService: ParameterService,
    private auth: CognitoService,
    private spinner: SpinnerService,
    private mediosCripto: MediosCriptoService,
  ) {
    this.getEstadoOperacion('USDT');
    this.getEstadoOperacion('FIAT_STOP');
    this.getEstadoOperacion('USDT_STOP');
    this.getEstadoOperacion('FIAT');
    this.getEstadoOperacion('ZELLE_STOP');
    this.getEstadoOperacion('ZELLE');
    this.getEstadoOperacion('AC');
    this.getEstadoBinance();
    this.getPagoMovilGlobal();
    this.getPagoMovilBanesco();
    this.cargarRedes();
  }

  /** Lectura directa (sin caché): el operador tiene que ver el estado real. */
  async cargarRedes(): Promise<void> {
    try {
      this.redesCripto = await this.mediosCripto.consultar();
      this.errorRedes = false;
    } catch (error) {
      this.redesCripto = null;
      this.errorRedes = true;
    }
  }

  nombreRed(red: MedioCripto): string {
    return etiquetaRed(red.id, red.descripcion);
  }

  /**
   * Pausa o activa una red, previa confirmación que explica qué cambia para
   * el cliente. Lo ya creado no se toca: solo se cortan las altas y las
   * transacciones nuevas en esa red (los backends responden 422).
   */
  async cambiarEstadoRed(red: MedioCripto, activo: boolean): Promise<void> {
    const confirmacion = await Swal.fire({
      icon: 'warning',
      title: activo ? `¿Activar la red ${this.nombreRed(red)}?` : `¿Pausar la red ${this.nombreRed(red)}?`,
      html: this.explicarCambioRed(red, activo),
      showCancelButton: true,
      confirmButtonText: activo ? 'Sí, activar' : 'Sí, pausar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: activo ? '#1C593D' : '#ca8a04',
      cancelButtonColor: '#6b7280',
    });
    if (!confirmacion.isConfirmed) return;

    const ref = this.spinner.start();
    try {
      await this.mediosCripto.cambiarEstado(red.id, activo);
      this.spinner.stop(ref);
    } catch (error: any) {
      this.spinner.stop(ref);
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo cambiar la red',
        text:
          error?.error?.message ||
          (typeof error === 'string' && error) ||
          'Intentá de nuevo en un momento.',
        confirmButtonColor: '#1C593D',
      });
    }
    // Siempre se vuelve a leer: con éxito trae quién y cuándo; con error,
    // el estado real (otro operador pudo haberla cambiado).
    await this.cargarRedes();
  }

  private explicarCambioRed(red: MedioCripto, activo: boolean): string {
    const nombre = this.nombreRed(red);
    if (activo) {
      const pago = red.pagaBinance
        ? 'Las transacciones por esta red las paga Binance, siempre que el Envío Binance esté activo.'
        : 'Las transacciones por esta red no las paga Binance: quedan en POR_PROCESAR para pagarlas a mano.';
      return (
        `Los clientes van a poder volver a registrar beneficiarios de ${nombre} ` +
        `y crear transacciones a esos beneficiarios.<br><br>${pago}`
      );
    }
    const retiros =
      red.id === MEDIO_CRIPTO_TRC20
        ? '<br><br>También se rechazan los retiros de USDT de los clientes (Enviar USDT y VES→USDT), que salen por esta red.'
        : '';
    return (
      `Mientras esté pausada, los clientes <strong>no van a poder dar de alta beneficiarios</strong> ` +
      `de ${nombre} <strong>ni crear transacciones</strong> a los que ya tienen. ` +
      `Van a seguir viendo, editando y eliminando esos beneficiarios.` +
      `<br><br>Lo que ya está creado sigue su curso normal.${retiros}`
    );
  }

  async saveEstadoOperacion(
    activo: boolean,
    tipo:
      | 'FIAT'
      | 'USDT'
      | 'ZELLE'
      | 'ZELLE_STOP'
      | 'FIAT_STOP'
      | 'USDT_STOP'
      | 'AC',
  ) {
    const ref = this.spinner.start();
    const cognitoUser = await this.auth.getUser();
    const email = cognitoUser.attributes.email;
    const body = {
      estado: activo,
      responsable_email: email,
      tipo,
    };
    await this.hoursOperationService.saveEstadoOperacion(body);
    this.spinner.stop(ref);
    await this.getEstadoOperacion(tipo);
  }

  async getEstadoOperacion(
    tipo:
      | 'FIAT'
      | 'USDT'
      | 'ZELLE'
      | 'ZELLE_STOP'
      | 'FIAT_STOP'
      | 'USDT_STOP'
      | 'AC',
  ) {
    const ref = this.spinner.start();
    this.estadoOperacion[tipo] =
      (await this.hoursOperationService.getEstadoOperacion(tipo)) ?? false;
    this.spinner.stop(ref);
  }

  async getEstadoBinance() {
    const ref = this.spinner.start();
    this.estadoBinance = await this.paramsService
      .getParameter('STOP_BINANCE')
      .then((res) => +res.valor === 1);
    this.spinner.stop(ref);
  }

  async saveEstadoBinance(status: number) {
    const ref = this.spinner.start();
    await this.paramsService.saveParameter('STOP_BINANCE', status.toString());
    this.estadoBinance = status === 1;
    this.spinner.stop(ref);
  }

  async getPagoMovilGlobal() {
    this.pagoMovilGlobal = await this.paramsService
      .getParameter('PAGO_MOVIL_GLOBAL')
      .then((res) => +res.valor === 1);
  }

  async savePagoMovilGlobal(status: number) {
    const ref = this.spinner.start();
    await this.paramsService.saveParameter(
      'PAGO_MOVIL_GLOBAL',
      status.toString(),
    );
    this.pagoMovilGlobal = status === 1;
    this.spinner.stop(ref);
  }

  async getPagoMovilBanesco() {
    this.pagoMovilBanesco = await this.paramsService
      .getParameter('PAGO_MOVIL_BANESCO')
      .then((res) => +res.valor === 1);
  }

  async savePagoMovilBanesco(status: number) {
    const ref = this.spinner.start();
    await this.paramsService.saveParameter(
      'PAGO_MOVIL_BANESCO',
      status.toString(),
    );
    this.pagoMovilBanesco = status === 1;
    this.spinner.stop(ref);
  }
}
